"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { defaultLocale, formatPrice, isValidLocale } from "@/config/locale";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";
import { usePricingResolvePresentations } from "@/lib/pricing/use-pricing-resolve-presentations";
import {
	STACK_BUILDER_QUANTITY_MAX,
	isStackBuilderVariantPurchasable,
} from "@/lib/stack-builder/purchasable-selection";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VariantPickerProps {
	variants: unknown[];
	selectedVariantId: string;
	quantity: number;
	onVariantChange: (variantId: string) => void;
	onQuantityChange: (quantity: number) => void;
	channel: string;
	locale: string;
	/** Checkout currency (EUR/USD) for the active channel. */
	currency: string;
}

type VariantRecord = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getVariantPrice(variant: VariantRecord): number {
	const pricing = variant.pricing as Record<string, unknown> | undefined;
	if (!pricing) return 0;
	const price = pricing.price as Record<string, unknown> | undefined;
	if (!price) return 0;
	const gross = price.gross as Record<string, unknown> | undefined;
	if (!gross) return 0;
	return (gross.amount as number) ?? 0;
}

function getVariantName(variant: VariantRecord): string {
	return (variant.name as string) ?? "";
}

function getVariantId(variant: VariantRecord): string {
	return (variant.id as string) ?? "";
}

/** Returns true if the variant name looks like it contains a dosage (e.g. "2mg", "5 mg"). */
function hasDosage(name: string): boolean {
	return /\d+\s*mg/i.test(name);
}

/**
 * Extracts a dosage label from a variant name.
 * e.g. "BPC-157 2mg" → "2mg", "5 mg" → "5 mg"
 */
function extractDosage(name: string): string {
	const match = name.match(/(\d+\s*mg)/i);
	return match ? match[1] : name;
}

// ---------------------------------------------------------------------------
// Dosage group helpers
// ---------------------------------------------------------------------------

/**
 * Group variants into dosage groups. Each group has a dosage label and
 * the variants sorted by ascending quantity (pack size).
 *
 * If no variants have dosage names (no "mg" text), we treat everything
 * as a single dosage group.
 */
interface DosageGroup {
	dosageLabel: string;
	variants: VariantRecord[];
}

function groupVariantsByDosage(rawVariants: VariantRecord[]): DosageGroup[] {
	const allHaveDosage = rawVariants.some((v) => hasDosage(getVariantName(v)));

	if (!allHaveDosage) {
		return [{ dosageLabel: "", variants: rawVariants }];
	}

	const groups: Map<string, VariantRecord[]> = new Map();
	for (const v of rawVariants) {
		const name = getVariantName(v);
		const key = extractDosage(name);
		if (!groups.has(key)) groups.set(key, []);
		groups.get(key)!.push(v);
	}

	return Array.from(groups.entries()).map(([dosageLabel, variants]) => ({
		dosageLabel,
		variants,
	}));
}

// ---------------------------------------------------------------------------
// Detect pack size from variant name
// ---------------------------------------------------------------------------

/**
 * Try to extract a pack number from variant name.
 * Patterns: "3 Pack", "3x", "Triple", "x3" etc.
 * Falls back to index+1.
 */
function extractPackNumber(name: string, fallbackIndex: number): number {
	const packMatch = name.match(/(\d+)\s*(?:x|pack|vials?)/i);
	if (packMatch) return parseInt(packMatch[1], 10);
	const xMatch = name.match(/x\s*(\d+)/i);
	if (xMatch) return parseInt(xMatch[1], 10);
	return fallbackIndex + 1;
}

// ---------------------------------------------------------------------------
// Discount detection
// ---------------------------------------------------------------------------

/**
 * Check if there is a pack discount for a given set of variants belonging
 * to the same dosage group.
 *
 * Strategy: if there is a single-unit variant and a multi-unit variant,
 * compare (multiPrice / multiPack) < singlePrice → discount exists.
 */
function detectPackDiscount(
	variants: VariantRecord[],
): { originalTotal: number; discountedTotal: number; packNumber: number } | null {
	if (variants.length < 2) return null;

	// Find the single-unit variant (pack == 1)
	const withPacks = variants.map((v, i) => ({
		v,
		pack: extractPackNumber(getVariantName(v), i),
		price: getVariantPrice(v),
	}));

	const single = withPacks.find((x) => x.pack === 1);
	if (!single) return null;

	for (const item of withPacks) {
		if (item.pack <= 1) continue;
		const expectedTotal = single.price * item.pack;
		if (item.price < expectedTotal - 0.001) {
			return {
				originalTotal: expectedTotal,
				discountedTotal: item.price,
				packNumber: item.pack,
			};
		}
	}
	return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VariantPicker({
	variants,
	selectedVariantId,
	quantity,
	onVariantChange,
	onQuantityChange,
	channel,
	locale,
	currency,
}: VariantPickerProps) {
	const t = useTranslations("stackBuilder");
	const tCart = useTranslations("cart");

	const rawVariants = useMemo(() => variants as VariantRecord[], [variants]);

	const groups = useMemo(() => groupVariantsByDosage(rawVariants), [rawVariants]);

	// Determine which dosage group the currently selected variant belongs to
	const activeGroup = useMemo(() => {
		return groups.find((g) => g.variants.some((v) => getVariantId(v) === selectedVariantId)) ?? groups[0];
	}, [groups, selectedVariantId]);

	const selectedVariant = useMemo(
		() => rawVariants.find((v) => getVariantId(v) === selectedVariantId),
		[rawVariants, selectedVariantId],
	);

	const selectedPrice = selectedVariant ? getVariantPrice(selectedVariant) : 0;

	// Pack buttons for current dosage group
	const packButtons = useMemo(() => {
		if (!activeGroup) return [];
		return activeGroup.variants.map((v, i) => ({
			variantId: getVariantId(v),
			pack: extractPackNumber(getVariantName(v), i),
			price: getVariantPrice(v),
			available: isStackBuilderVariantPurchasable(v),
		}));
	}, [activeGroup]);

	// Detect discount for current dosage group
	const discountInfo = useMemo(() => {
		if (!activeGroup) return null;
		return detectPackDiscount(activeGroup.variants);
	}, [activeGroup]);

	// Find the selected pack button
	const selectedPack = packButtons.find((b) => b.variantId === selectedVariantId);

	// Show original price (strikethrough) if user selected the discounted multi-pack
	let strikePrice: number | null = null;
	if (discountInfo && selectedPack && selectedPack.pack === discountInfo.packNumber) {
		strikePrice = discountInfo.originalTotal;
	}

	const resolveItems = useMemo(() => {
		const items: Array<{ id: string; amount: number; currency: string }> = [];
		if (selectedVariant && currency) {
			items.push({ id: "line", amount: selectedPrice * quantity, currency });
		}
		if (strikePrice !== null) {
			items.push({ id: "strike", amount: strikePrice * quantity, currency });
		}
		return items;
	}, [selectedVariant, currency, selectedPrice, quantity, strikePrice]);

	const presMap = usePricingResolvePresentations(locale, channel, resolveItems);
	const linePres = presMap.get("line");
	const strikePres = presMap.get("strike");

	const multiDosage = groups.length > 1;

	return (
		<div className="flex flex-col gap-2">
			{/* Dosage selector — only rendered when multiple dosage levels exist */}
			{multiDosage && (
				<div className="flex items-center gap-2">
					<span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
						{t("dosage")}
					</span>
					<div className="flex flex-wrap gap-1">
						{groups.map((group) => {
							const isActive = activeGroup?.dosageLabel === group.dosageLabel;
							// When switching dosage, pick first variant of that group
							const firstPurchasableVariant = group.variants.find(isStackBuilderVariantPurchasable);
							const firstVariantId = firstPurchasableVariant
								? getVariantId(firstPurchasableVariant)
								: selectedVariantId;
							const groupAvailable = Boolean(firstPurchasableVariant);

							return (
								<button
									key={group.dosageLabel}
									type="button"
									onClick={() => onVariantChange(firstVariantId)}
									disabled={!groupAvailable}
									aria-disabled={!groupAvailable}
									className={cn(
										"rounded border px-2 py-0.5 text-[11px] font-medium transition-colors",
										isActive
											? "border-accent bg-accent text-accent-foreground"
											: "hover:border-accent/60 hover:bg-accent/5 border-border bg-background text-foreground",
										!groupAvailable && "cursor-not-allowed opacity-45",
									)}
								>
									{group.dosageLabel}
								</button>
							);
						})}
					</div>
				</div>
			)}

			{/* Pack size buttons */}
			{packButtons.length > 0 && (
				<div className="flex items-center gap-2">
					<span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
						{t("packSize")}
					</span>
					<div className="flex gap-1">
						{packButtons.map(({ variantId, pack, available }) => {
							const isActive = variantId === selectedVariantId;
							return (
								<button
									key={variantId}
									type="button"
									onClick={() => onVariantChange(variantId)}
									disabled={!available}
									aria-disabled={!available}
									className={cn(
										"h-7 w-7 rounded border text-[11px] font-semibold transition-colors",
										"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
										isActive
											? "border-accent bg-accent text-accent-foreground shadow-sm"
											: "hover:border-accent/60 hover:bg-accent/5 border-border bg-background text-foreground",
										!available && "cursor-not-allowed line-through opacity-45",
									)}
									aria-label={t("packQuantityAria", { pack })}
									aria-pressed={isActive}
								>
									{pack}
								</button>
							);
						})}
					</div>
				</div>
			)}

			{/* Quantity stepper */}
			<div className="flex items-center gap-2">
				<span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
					{t("quantityShort")}
				</span>
				<div className="flex items-center gap-1">
					<button
						type="button"
						onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
						disabled={quantity <= 1}
						className={cn(
							"flex h-6 w-6 items-center justify-center rounded border text-xs font-bold",
							"hover:border-accent/60 hover:bg-accent/5 transition-colors",
							"disabled:cursor-not-allowed disabled:opacity-40",
							"border-border bg-background text-foreground",
						)}
						aria-label={tCart("decreaseQuantity")}
					>
						−
					</button>
					<span className="min-w-[1.5rem] text-center text-xs font-semibold tabular-nums">{quantity}</span>
					<button
						type="button"
						onClick={() => onQuantityChange(quantity + 1)}
						disabled={quantity >= STACK_BUILDER_QUANTITY_MAX}
						className={cn(
							"flex h-6 w-6 items-center justify-center rounded border text-xs font-bold",
							"hover:border-accent/60 hover:bg-accent/5 transition-colors",
							"disabled:cursor-not-allowed disabled:opacity-40",
							"border-border bg-background text-foreground",
						)}
						aria-label={tCart("increaseQuantity")}
					>
						+
					</button>
				</div>
			</div>

			{/* Price display */}
			<div className="flex items-baseline gap-1.5">
				<span className="text-sm font-semibold text-foreground">
					{linePres ? (
						<ReferencePrimaryPrice
							presentation={linePres}
							locale={locale}
							channel={channel}
							size="sm"
							className="text-sm font-semibold text-foreground"
						/>
					) : (
						formatPrice(selectedPrice * quantity, currency, isValidLocale(locale) ? locale : defaultLocale)
					)}
				</span>
				{strikePrice !== null && (
					<span className="text-xs text-muted-foreground line-through">
						{strikePres ? (
							<ReferencePrimaryPrice
								presentation={strikePres}
								locale={locale}
								channel={channel}
								size="sm"
								className="text-xs text-muted-foreground line-through"
								showReferenceTooltip={false}
							/>
						) : (
							formatPrice(strikePrice * quantity, currency, isValidLocale(locale) ? locale : defaultLocale)
						)}
					</span>
				)}
			</div>
		</div>
	);
}
